import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Download, FileText, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { format, subMonths } from "date-fns";
import { BestClientsReport, LapsedClientsReport, RetentionCohortsReport } from "@/components/AdvancedReports";
import TargetsVsActualsReport from "@/components/TargetsVsActualsReport";

function fmtGBP(v) {
  const n = Number(v || 0);
  return `£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const METHOD_COLORS = {
  'Stripe (Card)': 'bg-violet-100 text-violet-700 border-violet-200',
  'Card': 'bg-blue-100 text-blue-700 border-blue-200',
  'Cash': 'bg-green-100 text-green-700 border-green-200',
  'Bank Transfer': 'bg-amber-100 text-amber-700 border-amber-200',
  'Voucher': 'bg-pink-100 text-pink-700 border-pink-200',
  'Other': 'bg-gray-100 text-gray-700 border-gray-200',
  'Unspecified': 'bg-gray-100 text-gray-500 border-gray-200',
};
const methodDot = {
  'Stripe (Card)': '#8b5cf6', 'Card': '#3b82f6', 'Cash': '#22c55e',
  'Bank Transfer': '#f59e0b', 'Voucher': '#ec4899', 'Other': '#6b7280', 'Unspecified': '#9ca3af',
};

export default function Reports() {
  const [dateFrom, setDateFrom] = useState(format(subMonths(new Date(), 3), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [pnl, setPnl] = useState(null);
  const [sales, setSales] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [groupBy, setGroupBy] = useState('service');
  const [loading, setLoading] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  const toggleCat = (cat) => setExpandedCats(p => ({ ...p, [cat]: !p[cat] }));

  const loadPnl = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/reports/pnl?date_from=${dateFrom}&date_to=${dateTo}`); setPnl(res.data); } catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  const loadSales = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/reports/sales?date_from=${dateFrom}&date_to=${dateTo}&group_by=${groupBy}`); setSales(res.data.report || []); } catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  const loadPaymentMethods = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/reports/payment-methods?date_from=${dateFrom}&date_to=${dateTo}`); setPaymentMethods(res.data); } catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  const loadTransactions = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/reports/transaction-details?date_from=${dateFrom}&date_to=${dateTo}`); setTransactions(res.data); } catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  const exportPnlCSV = () => {
    if (!pnl) return;
    const items = pnl.expense_items_by_category || {};
    const rows = [
      ['Profit & Loss Report', `${dateFrom} to ${dateTo}`],
      [],
      ['Revenue', pnl.revenue.toFixed(2)],
      [],
      ['Expenses'],
    ];
    Object.entries(pnl.expenses_by_category || {}).forEach(([cat, amt]) => {
      rows.push([`  ${cat}`, amt.toFixed(2)]);
      // Indented line items per category so the CSV mirrors the on-screen breakdown
      (items[cat] || []).forEach(it => {
        const desc = (it.notes || '').replace(/"/g, '""');
        rows.push([
          `    ${it.date}`,
          `"${it.supplier_name || ''}"`,
          `"${it.invoice_number || ''}"`,
          `"${desc}"`,
          it.amount.toFixed(2),
        ]);
      });
    });
    rows.push([], ['Total Expenses', pnl.total_expenses.toFixed(2)], [], ['Net Profit', pnl.net_profit.toFixed(2)]);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `pnl-${dateFrom}-${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportSalesCSV = () => {
    if (!sales || !sales.length) return;
    const headers = Object.keys(sales[0]);
    const csv = [headers.join(','), ...sales.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `sales-${groupBy}-${dateFrom}-${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPaymentMethodsCSV = () => {
    if (!paymentMethods || !paymentMethods.months?.length) return;
    const allMethods = Object.keys(paymentMethods.method_totals || {});
    const rows = [['Month', ...allMethods, 'Total']];
    paymentMethods.months.forEach(m => {
      const byMethod = Object.fromEntries((m.breakdown || []).map(b => [b.method, b.amount]));
      rows.push([m.month_label, ...allMethods.map(meth => (byMethod[meth] ?? 0).toFixed(2)), m.total.toFixed(2)]);
    });
    rows.push(['Grand Total', ...allMethods.map(meth => (paymentMethods.method_totals[meth] ?? 0).toFixed(2)), paymentMethods.grand_total.toFixed(2)]);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `payment-methods-${dateFrom}-${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportTransactionsCSV = () => {
    if (!transactions || !transactions.rows?.length) return;
    const rows = [['Date', 'Client', 'Service(s)', 'Gross Amount', 'Discount/Voucher/Promo', 'Net Paid', 'Payment Method']];
    transactions.rows.forEach(r => {
      rows.push([r.date, `"${(r.client_name || '').replace(/"/g, '""')}"`, `"${(r.services || '').replace(/"/g, '""')}"`, r.gross_amount.toFixed(2), r.discount.toFixed(2), r.net_paid.toFixed(2), r.payment_method]);
    });
    rows.push(['', '', 'Totals', transactions.total_gross.toFixed(2), transactions.total_discount.toFixed(2), transactions.total_net_paid.toFixed(2), '']);
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `transaction-details-${dateFrom}-${dateTo}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Group expenses into Operating vs COGS
  const getExpenseGroups = () => {
    if (!pnl) return { cogs: {}, operating: {} };
    const cogs = {};
    const operating = {};
    Object.entries(pnl.expenses_by_category || {}).forEach(([cat, amt]) => {
      if (cat.toLowerCase().includes('product') || cat.toLowerCase().includes('cost of goods') || cat.toLowerCase().includes('inventory')) {
        cogs[cat] = amt;
      } else {
        operating[cat] = amt;
      }
    });
    return { cogs, operating };
  };

  return (
    <div className="space-y-5 fade-in">
      <Toaster position="top-center" />
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Reports</h1>

      <Card className="p-4 rounded-2xl border">
        <div className="flex flex-wrap gap-4 items-end">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1 rounded-xl" /></div>
        </div>
      </Card>

      <Tabs defaultValue="pnl">
        <TabsList className="rounded-xl">
          <TabsTrigger value="pnl" className="rounded-lg" data-testid="reports-tab-pnl">P&L Report</TabsTrigger>
          <TabsTrigger value="sales" className="rounded-lg" data-testid="reports-tab-sales">Sales Report</TabsTrigger>
          <TabsTrigger value="payment-methods" className="rounded-lg" data-testid="reports-tab-payment-methods">Payment Methods</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-lg" data-testid="reports-tab-transactions">Transaction Details</TabsTrigger>
          <TabsTrigger value="targets" className="rounded-lg" data-testid="reports-tab-targets">Targets vs Actuals</TabsTrigger>
          <TabsTrigger value="best" className="rounded-lg" data-testid="reports-tab-best">Best Clients</TabsTrigger>
          <TabsTrigger value="lapsed" className="rounded-lg" data-testid="reports-tab-lapsed">Lapsed Clients</TabsTrigger>
          <TabsTrigger value="cohorts" className="rounded-lg" data-testid="reports-tab-cohorts">Retention Cohorts</TabsTrigger>
        </TabsList>

        <TabsContent value="pnl" className="mt-4 space-y-4">
          <Button onClick={loadPnl} disabled={loading} className="rounded-xl"><FileText size={16} className="mr-1" /> Generate P&L</Button>
          {pnl && (() => {
            const expenses = pnl.expenses_by_category || {};
            return (
              <Card className="p-6 rounded-2xl border shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>Profit & Loss</h3>
                    <p className="text-xs text-muted-foreground">{dateFrom} to {dateTo}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={exportPnlCSV} className="rounded-xl"><Download size={14} className="mr-1" /> Export CSV</Button>
                </div>
                <div className="space-y-1">
                  {/* Revenue */}
                  <div className="flex justify-between items-center py-3 border-b-2 border-border">
                    <span className="font-semibold text-base" style={{ fontFamily: 'Playfair Display' }}>Revenue</span>
                    <span className="text-lg font-semibold text-green-600 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{fmtGBP(pnl.revenue)}</span>
                  </div>

                  {/* Operating Expenses */}
                  {Object.keys(expenses).length > 0 && (
                    <>
                      <div className="pt-3 pb-1"><span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Operating Expenses</span></div>
                      {Object.entries(expenses).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                        const items = (pnl.expense_items_by_category || {})[cat] || [];
                        const open = !!expandedCats[cat];
                        return (
                          <div key={cat} className="text-sm">
                            <button
                              type="button"
                              onClick={() => toggleCat(cat)}
                              disabled={items.length === 0}
                              className={`w-full flex justify-between items-center py-1.5 pl-4 pr-2 rounded-lg hover:bg-muted/40 transition-colors ${items.length === 0 ? 'cursor-default' : ''}`}
                              data-testid={`pnl-expense-row-${cat}`}
                            >
                              <span className="flex items-center gap-1.5 text-muted-foreground">
                                {items.length > 0 && (
                                  open
                                    ? <ChevronDown size={13} className="text-muted-foreground/70" />
                                    : <ChevronRight size={13} className="text-muted-foreground/70" />
                                )}
                                {cat}
                                {items.length > 0 && (
                                  <span className="text-[10px] text-muted-foreground/70">· {items.length} item{items.length !== 1 ? 's' : ''}</span>
                                )}
                              </span>
                              <span className="tabular-nums text-red-600">-{fmtGBP(amt)}</span>
                            </button>
                            {open && items.length > 0 && (
                              <div className="ml-7 mr-2 mb-2 mt-1 rounded-xl border border-border/60 overflow-hidden" data-testid={`pnl-expense-items-${cat}`}>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-muted/30 text-muted-foreground">
                                      <th className="text-left p-2 font-medium">Date</th>
                                      <th className="text-left p-2 font-medium">Supplier</th>
                                      <th className="text-left p-2 font-medium">Invoice #</th>
                                      <th className="text-left p-2 font-medium">Details</th>
                                      <th className="text-right p-2 font-medium">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map(it => (
                                      <tr key={it.id} className="border-t border-border/60">
                                        <td className="p-2 tabular-nums whitespace-nowrap">{it.date}</td>
                                        <td className="p-2">{it.supplier_name || <span className="text-muted-foreground/60">—</span>}</td>
                                        <td className="p-2 font-mono text-[11px]">
                                          {it.invoice_file_id ? (
                                            <a
                                              href={`${API}/invoices/file/${it.invoice_file_id}`}
                                              target="_blank"
                                              rel="noopener"
                                              className="text-primary hover:underline inline-flex items-center gap-1"
                                              title="Open source invoice"
                                              data-testid={`pnl-invoice-link-${it.id}`}
                                            >
                                              {it.invoice_number || 'View invoice'}
                                              <ExternalLink size={10} />
                                            </a>
                                          ) : (
                                            it.invoice_number || <span className="text-muted-foreground/60">—</span>
                                          )}
                                        </td>
                                        <td className="p-2 text-muted-foreground/90">{it.notes || <span className="text-muted-foreground/60">—</span>}</td>
                                        <td className="p-2 text-right tabular-nums text-red-600">-{fmtGBP(it.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Total Expenses */}
                  <div className="flex justify-between items-center py-2 border-t border-border mt-1">
                    <span className="font-semibold">Total Expenses</span>
                    <span className="text-base font-semibold tabular-nums text-red-600" style={{ fontFamily: 'Playfair Display' }}>-{fmtGBP(pnl.total_expenses)}</span>
                  </div>

                  {/* Net Profit */}
                  <div className="flex justify-between items-center py-3 border-t-2 border-border bg-muted/20 px-3 rounded-lg mt-2">
                    <span className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display' }}>Net Profit</span>
                    <span className={`text-2xl font-semibold tabular-nums ${pnl.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{ fontFamily: 'Playfair Display' }}>
                      {pnl.net_profit >= 0 ? '' : '-'}{fmtGBP(Math.abs(pnl.net_profit))}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })()}
        </TabsContent>

        <TabsContent value="sales" className="mt-4 space-y-4">
          <div className="flex gap-3">
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-40 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="service">By Service</SelectItem>
                <SelectItem value="staff">By Staff</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={loadSales} disabled={loading} className="rounded-xl"><FileText size={16} className="mr-1" /> Generate</Button>
          </div>
          {sales && (
            <Card className="rounded-2xl border shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>Sales by {groupBy}</h3>
                <Button variant="outline" size="sm" onClick={exportSalesCSV} className="rounded-xl"><Download size={14} className="mr-1" /> Export</Button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-3 font-medium text-muted-foreground">{groupBy === 'service' ? 'Service' : 'Staff'}</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Count</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((row, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="p-3 font-medium">{row.name}</td>
                      <td className="p-3 tabular-nums">{row.count}</td>
                      <td className="p-3 font-medium tabular-nums">{fmtGBP(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payment-methods" className="mt-4 space-y-4">
          <Button onClick={loadPaymentMethods} disabled={loading} className="rounded-xl" data-testid="generate-payment-methods-btn"><FileText size={16} className="mr-1" /> Generate</Button>
          {paymentMethods && (
            <Card className="rounded-2xl border shadow-sm overflow-hidden" data-testid="payment-methods-report">
              <div className="flex items-center justify-between p-4">
                <div>
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>How sales were receipted</h3>
                  <p className="text-xs text-muted-foreground">{dateFrom} to {dateTo} · based on Completed/Confirmed bookings</p>
                </div>
                <Button variant="outline" size="sm" onClick={exportPaymentMethodsCSV} className="rounded-xl"><Download size={14} className="mr-1" /> Export</Button>
              </div>

              {/* Overall method totals */}
              <div className="flex flex-wrap gap-2 px-4 pb-4">
                {Object.entries(paymentMethods.method_totals || {}).sort((a, b) => b[1] - a[1]).map(([method, amt]) => (
                  <div key={method} className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${METHOD_COLORS[method] || METHOD_COLORS.Other}`} data-testid={`method-total-${method.replace(/\s|\(|\)/g, '')}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: methodDot[method] || methodDot.Other }} />
                    <span className="font-medium">{method}</span>
                    <span className="tabular-nums">{fmtGBP(amt)}</span>
                  </div>
                ))}
                {paymentMethods.months?.length > 0 && (
                  <div className="text-xs px-3 py-1.5 rounded-full border bg-muted/40 font-semibold tabular-nums" data-testid="method-grand-total">
                    Grand Total: {fmtGBP(paymentMethods.grand_total)}
                  </div>
                )}
              </div>

              {paymentMethods.months?.length === 0 ? (
                <p className="px-4 pb-6 text-sm text-muted-foreground">No completed or confirmed bookings in this date range.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-3 font-medium text-muted-foreground">Month</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Breakdown</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">Total Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentMethods.months.map(m => (
                      <tr key={m.month} className="border-b border-border/50 align-top" data-testid={`payment-method-row-${m.month}`}>
                        <td className="p-3 font-medium whitespace-nowrap">{m.month_label}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1.5">
                            {m.breakdown.map(b => (
                              <span key={b.method} className={`text-[11px] px-2 py-1 rounded-full border ${METHOD_COLORS[b.method] || METHOD_COLORS.Other}`}>
                                {b.method}: {fmtGBP(b.amount)} <span className="opacity-60">({b.pct}%)</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 text-right font-semibold tabular-nums">{fmtGBP(m.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="transactions" className="mt-4 space-y-4">
          <Button onClick={loadTransactions} disabled={loading} className="rounded-xl" data-testid="generate-transactions-btn"><FileText size={16} className="mr-1" /> Generate</Button>
          {transactions && (
            <Card className="rounded-2xl border shadow-sm overflow-hidden" data-testid="transaction-details-report">
              <div className="flex items-center justify-between p-4">
                <div>
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>Transaction Details</h3>
                  <p className="text-xs text-muted-foreground">{dateFrom} to {dateTo} · based on Completed/Confirmed bookings</p>
                </div>
                <Button variant="outline" size="sm" onClick={exportTransactionsCSV} className="rounded-xl" data-testid="export-transactions-btn"><Download size={14} className="mr-1" /> Export</Button>
              </div>

              <div className="flex flex-wrap gap-2 px-4 pb-4">
                <div className="text-xs px-3 py-1.5 rounded-full border bg-muted/40 flex items-center gap-1.5" data-testid="txn-total-gross">
                  <span className="font-medium">Gross (before discount/voucher)</span>
                  <span className="tabular-nums font-semibold">{fmtGBP(transactions.total_gross)}</span>
                </div>
                <div className="text-xs px-3 py-1.5 rounded-full border bg-muted/40 flex items-center gap-1.5" data-testid="txn-total-discount">
                  <span className="font-medium">Discount/Voucher/Promo</span>
                  <span className="tabular-nums font-semibold">-{fmtGBP(transactions.total_discount)}</span>
                </div>
                <div className="text-xs px-3 py-1.5 rounded-full border bg-muted/40 flex items-center gap-1.5" data-testid="txn-total-net-paid">
                  <span className="font-medium">Net Paid</span>
                  <span className="tabular-nums font-semibold">{fmtGBP(transactions.total_net_paid)}</span>
                </div>
              </div>

              {transactions.rows?.length === 0 ? (
                <p className="px-4 pb-6 text-sm text-muted-foreground">No completed or confirmed bookings in this date range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Client</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Service(s)</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Gross</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Discount</th>
                        <th className="text-right p-3 font-medium text-muted-foreground">Net Paid</th>
                        <th className="text-left p-3 font-medium text-muted-foreground">Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.rows.map((r, idx) => (
                        <tr key={idx} className="border-b border-border/50 align-top" data-testid={`transaction-row-${idx}`}>
                          <td className="p-3 whitespace-nowrap">{(r.date || '').slice(0, 16).replace('T', ' ')}</td>
                          <td className="p-3 font-medium whitespace-nowrap">{r.client_name}</td>
                          <td className="p-3">{r.services}</td>
                          <td className="p-3 text-right tabular-nums">{fmtGBP(r.gross_amount)}</td>
                          <td className="p-3 text-right tabular-nums text-rose-600">{r.discount > 0 ? `-${fmtGBP(r.discount)}` : '—'}</td>
                          <td className="p-3 text-right font-semibold tabular-nums">{fmtGBP(r.net_paid)}</td>
                          <td className="p-3">
                            <span className={`text-[11px] px-2 py-1 rounded-full border ${METHOD_COLORS[r.payment_method] || METHOD_COLORS.Other}`}>{r.payment_method}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="best" className="mt-4">
          <BestClientsReport />
        </TabsContent>

        <TabsContent value="targets" className="mt-4">
          <TargetsVsActualsReport />
        </TabsContent>

        <TabsContent value="lapsed" className="mt-4">
          <LapsedClientsReport />
        </TabsContent>

        <TabsContent value="cohorts" className="mt-4">
          <RetentionCohortsReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
